import cx from 'classnames'
import Styles from 'components/WorkspaceDashboard/WorkspaceDashboard.module.scss'
import WriterStyles from './WriterDashboard.module.scss'
import { UpgradePlanModal } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import { NewPaywallModal } from 'components/Paywall/NewPaywallModal/NewPaywallModal'
import { UpdateBlazePaymentMethodModal } from 'components/UpdateBlazePaymentMethodModal/UpdateBlazePaymentMethodModal'
import { WorkspaceDashboardSidebar } from 'blaze-components/WorkspaceDashboardSidebar/WorkspaceDashboardSidebar'
import { HandbookProvider } from 'components/Handbook2_0/Sidebar/HandbookProvider'
import { ArticleView, RootState, UserRole, Workspace } from 'types'
import { DocPresence, DocPresenceContext } from 'stores/DocPresence'
import { FocusModeNavbar } from 'components/WorkspaceDashboard/FocusModeNavbar'
import SettingsContainer from 'components/Settings/SettingsContainer'
import { FocusModeToast } from 'components/WorkspaceDashboard/FocusModeToast'
import * as Layout from 'components/Layout'
import { useUnregisteredUserBanner } from 'hooks/useUnregisteredUser'
import { useSuperAdmin } from 'hooks/useSuperAdmin'
import { useDispatch, useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import { useThrottledFn } from 'beautiful-react-hooks'
import { focusModeSet } from 'redux/actions/focusMode'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { useEnforceBlazeFreeTrial } from 'hooks/useEnforceBlazeFreeTrial'
import { useSidebarState } from 'blaze-components/WorkspaceDashboardSidebar/hooks/useSidebarState'
import { useHandbook } from 'components/Handbook2_0/hooks'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useArticleView } from 'providers/ArticleViewProvider/ArticleViewProvider'
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { PlaceholderRightRail } from './PlaceholderRightRail'
import { Can, CanView } from 'components/Can'
import { EditButton } from './EditButton'
import { WriterHeader } from 'blaze-components/WorkspaceDashboardHeader/WriterHeader'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { useWorkspace } from 'hooks/useWorkspace'
import { AIButton } from './AIButton'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { ReviewRequestCardWrapper } from './ReviewRequestCardWrapper'

const THROTTLE_FOCUS_MODE_MS = 1000
const HIDE_FOCUS_MODE_TOAST_KEY = 'HIDE_FOCUS_MODE_TOAST'

interface Props {
  workspace: Workspace
}

export const WriterDashboard = observer(({ children, workspace }: PropsWithChildren<Props>) => {
  const [showUnregisteredUserBanner] = useUnregisteredUserBanner()
  const { superAdminMode } = useSuperAdmin()
  const [style, setStyle] = useState({})
  const focusMode = useSelector(({ focusMode }: RootState) => focusMode, isEqual)
  const { isMobile } = useResponsiveDesign()
  focusMode.showFocusMode = !isMobile && focusMode.showFocusMode
  const [hideFocusModeToast, setHideFocusModeToast] = useState(
    isMobile || localStorage.getItem(HIDE_FOCUS_MODE_TOAST_KEY) === 'true',
  )
  const dispatch = useDispatch()
  const { docStore, leftSidebarStore, workspaceTheme, drawersStore } = useRootStore()
  const { showPaywall, onPaywallUpdateSuccess, hasOtherPayingWorkspaces, canStartFreeTrial } =
    useEnforceBlazeFreeTrial()
  const sidebarState = useSidebarState()
  const throttledResetFocusMode = useThrottledFn(() => {
    if (!focusMode.manual && focusMode.showFocusMode) {
      dispatch(focusModeSet({ showFocusMode: false }))
    }
  }, THROTTLE_FOCUS_MODE_MS)
  const handbook = useHandbook()
  const articleMeta = useCurrentArticleMeta()
  const articleView = useArticleView()
  const articleWorkspace = useWorkspace(articleMeta?.company_id || null)
  const docPresence = useRef<DocPresence | null>(null)
  const [previousFocusMode, setPreviousFocusMode] = useState(false)
  const { isChannelSocialPostable } = useIsChannelPostable()

  if (previousFocusMode !== focusMode.showFocusMode && !previousFocusMode && !hideFocusModeToast) {
    setPreviousFocusMode(focusMode.showFocusMode)
  }

  useEffect(() => {
    if (!articleMeta?.id) {
      return
    }
    docPresence.current = new DocPresence()
  }, [articleMeta?.id])

  const handleHideFocusModeToast = () => {
    setHideFocusModeToast(true)
    localStorage.setItem(HIDE_FOCUS_MODE_TOAST_KEY, 'true')
  }

  const authedArticle = {
    role: articleWorkspace?.role || UserRole.UNAUTHED,
    permissions: articleMeta?.almanac_file.permissions,
    __caslSubjectType__: 'Article',
  }

  const noPostPreview = [UserRole.UNAUTHED].includes(authedArticle.role)

  const viewportHandler = useCallback(() => {
    if (!isMobile) return

    const viewport = window.visualViewport
    const offsetTop = viewport?.offsetTop || 0

    setStyle({
      transform: `translateY(${offsetTop}px)`,
      height: `calc(100dvh - ${offsetTop}px)`,
    })
  }, [isMobile])

  useEffect(() => {
    window.visualViewport?.addEventListener('scroll', viewportHandler)
    window.visualViewport?.addEventListener('resize', viewportHandler)

    return () => {
      window.visualViewport?.removeEventListener('scroll', viewportHandler)
      window.visualViewport?.removeEventListener('resize', viewportHandler)
    }
  }, [viewportHandler])

  return (
    <div
      data-focus-mode={focusMode.showFocusMode ? 'on' : 'off'}
      data-view-mode={articleView || ArticleView.Reading}
      className={cx(Styles.dashboard, Styles.writerDashboard, {
        [Styles.withUnregisteredBanner]: showUnregisteredUserBanner,
        [Styles.inSuperAdminMode]: superAdminMode,
        [Styles.rightRailOpen]: !!docStore?.rightRailStore.locked,
      })}
      onMouseMove={focusMode.showFocusMode ? throttledResetFocusMode : undefined}
      id="workspace-dashboard"
      style={{
        ...workspaceTheme.paletteProperties(['--sidebar-background-color']),
        ...style,
      }}
    >
      {showPaywall && !canStartFreeTrial && (
        <UpgradePlanModal
          onClose={() => {}}
          showSuccessModal={false}
          onSuccess={onPaywallUpdateSuccess}
          showWorkspacesLink={hasOtherPayingWorkspaces}
          initialBillingFrequency="month"
        />
      )}
      {showPaywall && canStartFreeTrial && <NewPaywallModal />}
      {workspace?.paying && workspace?.payment_failed && <UpdateBlazePaymentMethodModal />}
      {sidebarState.isVisible && <WorkspaceDashboardSidebar />}
      <div className={Styles.innerContent}>
        <DocPresenceContext.Provider value={docPresence.current}>
          <WriterHeader workspace={workspace} />
          <HandbookProvider handbookKey={handbook?.key}>
            <div
              className={cx(Styles.mainContainer, Styles.notCore, Styles.isBlazeEditor, {
                [Styles.isExternal]: articleMeta?.external_link,
                [Styles.notEditing]: articleView !== ArticleView.Editing,
              })}
            >
              {focusMode.showFocusMode && <FocusModeNavbar />}
              <div
                className={cx(Styles.contentWrapper, {
                  [Styles.inEditorView]: !!articleView,
                  [Styles.noPostPreview]: noPostPreview,
                })}
                id="dashboard-content-wrapper"
                onMouseEnter={() => leftSidebarStore.setIsHovered(false)}
              >
                <CanView inWorkspace={workspace.id} inViewMode={[ArticleView.Reading, ArticleView.Commenting]}>
                  <ResponsiveDesign.Mobile>
                    {articleMeta && (
                      <ReviewRequestCardWrapper fileId={articleMeta.almanac_file.id} channel={articleMeta.channel} />
                    )}
                  </ResponsiveDesign.Mobile>
                </CanView>
                {children}
                <SettingsContainer workspace={workspace} />
                {previousFocusMode && !hideFocusModeToast && <FocusModeToast onClose={handleHideFocusModeToast} />}
              </div>
            </div>
          </HandbookProvider>
        </DocPresenceContext.Provider>
        {isMobile &&
          articleWorkspace &&
          articleMeta &&
          docStore &&
          (!isChannelSocialPostable(articleMeta.channel) || noPostPreview) && (
            <CanView inWorkspace={workspace.id} inViewMode={[ArticleView.Reading, ArticleView.Commenting]}>
              <Can a={authedArticle} I="edit" inWorkspace={articleWorkspace.id}>
                <div className={Styles.editButtonContainer}>
                  <AIButton drawersStore={drawersStore} />
                </div>
              </Can>
            </CanView>
          )}
        {isMobile &&
          articleWorkspace &&
          articleMeta &&
          (!isChannelSocialPostable(articleMeta.channel) || noPostPreview) && (
            <CanView inWorkspace={workspace.id} inViewMode={[ArticleView.Reading, ArticleView.Commenting]}>
              <Can a={authedArticle} I="edit" inWorkspace={articleWorkspace.id}>
                <div className={Styles.editButtonContainer}>
                  <EditButton />
                </div>
              </Can>
            </CanView>
          )}
      </div>
      <Layout.RightColumnContainer className={WriterStyles[`rightColumn${articleView || ArticleView.Reading}`]}>
        <PlaceholderRightRail />
      </Layout.RightColumnContainer>
    </div>
  )
})
